import { useCallback } from 'react';
import { useLocation } from 'react-router';
import { NavigationSource } from '../../common/enums/NavigationSource';
import { UIViewEnum } from '../../common/enums/UIViewEnum';
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { getDeviceRoute, getGroupRoute, getTagRoute, getTankBatteryRoute, getTankRoute, getViewIDFromPath, getWellRoute } from '../../common/utilities/RouteHelpers';
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { useUserDefaultViews } from '../../queries/user-default-view/UseUserDefaultViews';
import { UserState, useUserContext } from '../../contexts/user-context/UserContext';
import { IUIDefaultViewModel } from '../../services/ui-personalization-data-service/Models';

export default function useNavigateToView() {
    const routeContext = useRouteContext();
    const location = useLocation();
    const navigateTo = useNavigateTo();
    const [offlineState] = useOfflineDetectContext();
    const [userState] = useUserContext();

    const { data: defaultViews } = useQueryNotifications(useUserDefaultViews)({
        enabled: !offlineState.offline
    });

    const navigateToView = useCallback((
        objectType: eLynxObjectTypeEnum,
        objectID: number,
        viewID: number | undefined,
        navigationSource: NavigationSource,
        options?: NavigateToOptions
    ) => {
        let url: string | undefined;

        switch (objectType) {
            case eLynxObjectTypeEnum.Group:

                url = getGroupRoute(
                    groupViewID(userState, defaultViews, objectID, viewID),
                    objectID
                );
                break;

            case eLynxObjectTypeEnum.Device:

                url = getDeviceRoute(
                    viewID ?? (routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Device
                        ? getViewIDFromPath(location.pathname, UIViewEnum.Device_Details)
                        : UIViewEnum.Device_Details),
                    routeContext.groupID,
                    routeContext.wellID,
                    objectID,
                    false
                );
                break;

            case eLynxObjectTypeEnum.Well:

                url = getWellRoute(
                    viewID ?? (routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Well
                        ? getViewIDFromPath(location.pathname, UIViewEnum.Well_Details)
                        : UIViewEnum.Well_Details),
                    routeContext.groupID,
                    objectID
                );
                break;

            case eLynxObjectTypeEnum.Tank:

                url = getTankRoute(
                    viewID ?? (routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tank
                        ? getViewIDFromPath(location.pathname, UIViewEnum.Tank_Details)
                        : UIViewEnum.Tank_Details),
                    routeContext.groupID,
                    routeContext.deviceID,
                    routeContext.tankBatteryID,
                    objectID
                );
                break;

            case eLynxObjectTypeEnum.TankBattery:

                url = getTankBatteryRoute(
                    viewID ?? (routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.TankBattery
                        ? getViewIDFromPath(location.pathname, UIViewEnum.TankBattery_Details)
                        : UIViewEnum.TankBattery_Details),
                    routeContext.groupID,
                    objectID
                );
                break;

            case eLynxObjectTypeEnum.Tag:

                url = getTagRoute(
                    viewID ?? (routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tag
                        ? getViewIDFromPath(location.pathname, UIViewEnum.Tag_Details)
                        : UIViewEnum.Tag_Details),
                    routeContext.groupID,
                    routeContext.wellID,
                    routeContext.deviceID,
                    routeContext.tankBatteryID,
                    routeContext.tankID,
                    objectID
                );
                break;

            default:
                throw new Error(`Unable to navigate to object type: ${objectType.toString()}`);
        }

        url = `${url}&navSrc=${navigationSource}`

        navigateTo(url, options);
    }, [
        navigateTo,
        location.pathname,
        routeContext
    ]);

    return navigateToView;
}

function groupViewID(
    userState: UserState,
    defaultViews: Array<IUIDefaultViewModel> | undefined,
    groupID: number,
    fallbackViewID: UIViewEnum = UIViewEnum.Group_SummaryListing
): UIViewEnum {
    const vid = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && o.user?.id === userState.user?.id && !o.objectID)?.viewID ??
        defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && o.objectID === groupID && o.user?.id === userState.user?.id)?.viewID ??
        defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && o.objectID === groupID && !o.user?.id)?.viewID ??
        defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && !o.user?.id && !o.objectID)?.viewID ??
        fallbackViewID;

    return vid;
}